import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { createPasskey, isFramed, passkeysSupported, mfaErr } from "../../lib/webauthn";

const box = "bg-white border border-erf-borderCream rounded-md p-4 space-y-3";
const input = "min-h-[44px] w-full rounded border border-erf-borderCream px-3 text-sm focus:outline-none focus:ring-2 focus:ring-erf-gold";
const hdr = (t) => (t ? { headers: { "X-MFA-StepUp": t } } : {});
const day = (s) => (s ? String(s).slice(0, 10) : "never");

function PasswordField({ value, onChange, id }) {
  return (
    <label htmlFor={id} className="block text-xs">Current password (needed for your first sign-in method)
      <input id={id} type="password" value={value} onChange={(e) => onChange(e.target.value)} className={input} data-testid={id} />
    </label>
  );
}

export function PasskeysSection({ status, token, firstFactor, reload }) {
  const [name, setName] = useState("iPhone");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const canAdd = status.passkeys_configured && passkeysSupported() && !isFramed();
  const add = async () => {
    setBusy(true);
    try {
      const o = (await api.post("/mfa/passkeys/register/options", firstFactor ? { password: pw } : {}, hdr(token))).data;
      await api.post("/mfa/passkeys/register/verify", { challenge_id: o.challenge_id, credential: await createPasskey(o.options), name });
      toast.success("Passkey added"); setPw(""); reload();
    } catch (e) { toast.error(mfaErr(e)); } finally { setBusy(false); }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(mfaErr(e)); } };
  return (
    <section className={box} aria-labelledby="sec-passkeys" data-testid="security-passkeys">
      <h2 id="sec-passkeys" className="font-nav text-sm font-semibold">Passkeys (Face ID / Touch ID)</h2>
      <p className="text-xs" data-testid="security-passkeys-state">
        {status.passkeys_active_for_this_site ? "Passkeys active on this site." : status.passkeys_configured ? "No passkey for this site yet." : "Passkeys aren't configured for this site yet."}
      </p>
      <ul className="grid gap-2" data-testid="security-passkeys-list">
        {status.passkeys.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 border border-erf-borderCream rounded p-2 text-sm" data-testid={`security-passkey-${c.id}`}>
            <span><strong>{c.name}</strong> <span className="text-xs text-erf-dark/70">added {day(c.created_at)} · last used {day(c.last_used_at)}{c.usable_here ? "" : ` · registered on ${c.rp_id}, not usable here`}</span></span>
            <span className="flex gap-2">
              <Button type="button" variant="outline" className="min-h-[44px]" data-testid={`security-passkey-rename-${c.id}`}
                onClick={() => { const n = window.prompt("New name", c.name); if (n) act(() => api.patch(`/mfa/passkeys/${c.id}`, { name: n }), "Renamed"); }}>Rename</Button>
              <Button type="button" variant="outline" className="min-h-[44px]" disabled={!token} data-testid={`security-passkey-remove-${c.id}`}
                onClick={() => act(() => api.delete(`/mfa/passkeys/${c.id}`, hdr(token)), "Passkey removed")}>Remove</Button>
            </span>
          </li>
        ))}
      </ul>
      {canAdd && (
        <div className="grid gap-2 sm:flex sm:items-end">
          <label htmlFor="security-passkey-name" className="block text-xs sm:flex-1">Name<input id="security-passkey-name" value={name} onChange={(e) => setName(e.target.value)} className={input} data-testid="security-passkey-name" /></label>
          {firstFactor && <div className="sm:flex-1"><PasswordField id="security-passkey-password" value={pw} onChange={setPw} /></div>}
          <Button type="button" disabled={busy || (!firstFactor && !token) || (firstFactor && !pw)} onClick={add} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold" data-testid="security-passkey-add">Add passkey</Button>
        </div>
      )}
    </section>
  );
}

export function TotpSection({ status, token, firstFactor, reload }) {
  const [setup, setSetup] = useState(null);
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const start = async () => {
    try { setSetup((await api.post("/mfa/totp/setup", firstFactor ? { password: pw } : {}, hdr(token))).data); } catch (e) { toast.error(mfaErr(e)); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    try { await api.post("/mfa/totp/confirm", { code }); toast.success("Authenticator app is on"); setSetup(null); setCode(""); reload(); } catch (err) { toast.error(mfaErr(err)); }
  };
  const remove = async () => { try { await api.delete("/mfa/totp", hdr(token)); toast.success("Authenticator app removed"); reload(); } catch (e) { toast.error(mfaErr(e)); } };
  const active = status.totp.status === "active";
  return (
    <section className={box} aria-labelledby="sec-totp" data-testid="security-totp">
      <h2 id="sec-totp" className="font-nav text-sm font-semibold">Authenticator app</h2>
      <p className="text-xs" data-testid="security-totp-state">{active ? "On." : "Off."}</p>
      {active && <Button type="button" variant="outline" disabled={!token} onClick={remove} className="min-h-[44px]" data-testid="security-totp-remove">Remove</Button>}
      {!active && !setup && (
        <div className="grid gap-2 sm:flex sm:items-end">
          {firstFactor && <div className="sm:flex-1"><PasswordField id="security-totp-password" value={pw} onChange={setPw} /></div>}
          <Button type="button" onClick={start} disabled={(!firstFactor && !token) || (firstFactor && !pw)} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold" data-testid="security-totp-start">Set up</Button>
        </div>
      )}
      {setup && (
        <form onSubmit={confirm} className="grid gap-3 sm:grid-cols-[auto,1fr] sm:items-start" data-testid="security-totp-setup">
          <QRCodeSVG value={setup.otpauth_uri} size={168} aria-label="Authenticator QR code" data-testid="security-totp-qr" />
          <div className="space-y-2">
            <p className="text-xs">Scan with your authenticator app, or enter this key: <code className="break-all" data-testid="security-totp-secret">{setup.secret}</code></p>
            <label htmlFor="security-totp-code" className="block text-xs">6-digit code<input id="security-totp-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" className={input} data-testid="security-totp-code" /></label>
            <Button type="submit" disabled={code.trim().length !== 6} className="min-h-[44px] bg-erf-dark text-white" data-testid="security-totp-confirm">Turn on</Button>
          </div>
        </form>
      )}
    </section>
  );
}

export function RecoverySection({ status, token, hasFactor, reload }) {
  const [codes, setCodes] = useState(null);
  const gen = async () => { try { setCodes((await api.post("/mfa/recovery-codes", {}, hdr(token))).data.codes); reload(); } catch (e) { toast.error(mfaErr(e)); } };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`ERF ConstructionOS recovery codes (each works once)\n\n${codes.join("\n")}\n`], { type: "text/plain" }));
    a.download = "erf-recovery-codes.txt"; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <section className={box} aria-labelledby="sec-rec" data-testid="security-recovery">
      <h2 id="sec-rec" className="font-nav text-sm font-semibold">Recovery codes</h2>
      <p className="text-xs" data-testid="security-recovery-state">{status.recovery_codes.generated_at ? `${status.recovery_codes.remaining} of 10 left.` : "None yet."} Each code works once. Keep them somewhere safe.</p>
      {codes ? (
        <div className="space-y-2" data-testid="security-recovery-codes">
          <p className="text-xs font-semibold">Shown once. Save them now.</p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
          <Button type="button" variant="outline" onClick={download} className="min-h-[44px]" data-testid="security-recovery-download">Download</Button>
        </div>
      ) : (
        <Button type="button" disabled={!hasFactor || !token} onClick={gen} className="min-h-[44px] bg-erf-dark text-white" data-testid="security-recovery-generate">
          {status.recovery_codes.generated_at ? "Replace codes" : "Create codes"}
        </Button>
      )}
    </section>
  );
}

export function TrustedDevicesSection({ devices, reload }) {
  const revoke = async (id) => { try { await api.delete(id ? `/mfa/trusted-devices/${id}` : "/mfa/trusted-devices"); toast.success("Device forgotten"); reload(); } catch (e) { toast.error(mfaErr(e)); } };
  return (
    <section className={box} aria-labelledby="sec-dev" data-testid="security-devices">
      <h2 id="sec-dev" className="font-nav text-sm font-semibold">Remembered devices</h2>
      {devices.length === 0 ? <p className="text-xs" data-testid="security-devices-empty">No remembered devices.</p> : (
        <ul className="grid gap-2">
          {devices.map((d) => (
            <li key={d.id} className="flex flex-wrap justify-between gap-2 border border-erf-borderCream rounded p-2 text-xs" data-testid={`security-device-${d.id}`}>
              <span>{d.ua || "Browser"} · until {day(d.expires_at)} · last seen {day(d.last_seen_at)}</span>
              <Button type="button" variant="outline" className="min-h-[44px]" onClick={() => revoke(d.id)} data-testid={`security-device-revoke-${d.id}`}>Forget</Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
